import { Check, Minus } from 'lucide-react';
import { useId } from 'react';
import type { ChangeEvent, ComponentProps, ReactNode } from 'react';
import { cn } from '../lib/cn.ts';

export interface CheckboxProps extends Omit<ComponentProps<'input'>, 'type' | 'size' | 'checked' | 'onChange'> {
  checked: boolean;
  /** Some but not all (a select-all box): shows a dash, reads as "mixed". */
  indeterminate?: boolean;
  onCheckedChange: (checked: boolean, e: ChangeEvent<HTMLInputElement>) => void;
  /** Visible label beside the box. Without one, pass aria-label. */
  label?: ReactNode;
  /** One line under the label. */
  hint?: ReactNode;
  wrapperClassName?: string;
}

/**
 * A checkbox: a real input for keyboard and screen readers, drawn with tokens. For on/off
 * applied on Save, consent, multi-select and checklists (DesignSystem: selection controls).
 */
export function Checkbox({ checked, indeterminate, onCheckedChange, label, hint, disabled, className, wrapperClassName, id: idProp, ...rest }: CheckboxProps) {
  const auto = useId();
  const id = idProp ?? auto;
  const on = checked || indeterminate;
  const box = (
    <span className={cn('relative inline-flex size-(--checkbox-size) shrink-0', className)}>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        aria-checked={indeterminate ? 'mixed' : checked}
        ref={(el) => {
          if (el) el.indeterminate = !!indeterminate;
        }}
        onChange={(e) => onCheckedChange(e.target.checked, e)}
        className="peer absolute inset-0 m-0 cursor-pointer appearance-none rounded-(--checkbox-radius) opacity-0 disabled:cursor-not-allowed"
        {...rest}
      />
      {/* The drawn box: the same look as the Checkbox primitive (16 px, 4 px corner, control edge, the control colour with a white check when on). */}
      <span
        aria-hidden
        data-disabled={disabled || undefined}
        className={cn(
          'pointer-events-none flex size-(--checkbox-size) items-center justify-center rounded-(--checkbox-radius) border transition-colors duration-(--duration-fast) ease-standard',
          'peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-(--focus) peer-focus-visible:outline-solid',
          disabled
            ? on
              ? 'border-border-strong bg-active data-disabled:text-fg-faint'
              : 'border-border-strong bg-sunken'
            : on
              ? 'border-transparent bg-(--control-mid) text-(--control-fg)'
              : 'border-input bg-sunken peer-hover:border-fg-subtle',
        )}
      >
        {indeterminate ? <Minus className="size-3" strokeWidth={3} /> : checked ? <Check className="size-3" strokeWidth={3} /> : null}
      </span>
    </span>
  );
  if (label == null) return box;
  return (
    <div className={cn('flex items-start gap-2', wrapperClassName)}>
      <span className="mt-0.5 flex">{box}</span>
      <span className="flex min-w-0 flex-col">
        <label htmlFor={id} data-disabled={disabled || undefined} className="cursor-pointer text-ui text-fg data-disabled:cursor-not-allowed data-disabled:text-fg-faint">
          {label}
        </label>
        {hint != null && <span className="text-caption text-fg-subtle">{hint}</span>}
      </span>
    </div>
  );
}
